import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { EnquiryService } from '../../core/services/enquiry.service';
import { EnquiryItem, EnquiryStats } from '../../models/enquiry.interface';

@Component({
  selector: 'app-enquiry-list',
  standalone: true,
  imports: [CommonModule, FormsModule, SidebarComponent, NavbarComponent],
  template: `
    <app-sidebar></app-sidebar>
    <app-navbar
      pageTitle="Enquiries"
      pageSubtitle="Manage customer customization inquiries"
    ></app-navbar>

    <div class="page-wrapper">
      <main class="main-content animate-in">

        <!-- STAT CARDS -->
        <section class="stats-row">
          <div class="stat-card glass-card">
            <div class="icon-box icon-box-blue icon-box-sm">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </div>
            <div class="stat-info">
              <div class="stat-num">{{ stats.total }}</div>
              <div class="stat-lbl">Total Enquiries</div>
            </div>
          </div>

          <div class="stat-card glass-card gold-border-card">
            <div class="icon-box icon-box-gold icon-box-sm">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div class="stat-info">
              <div class="stat-num text-gold">{{ stats.pending }}</div>
              <div class="stat-lbl">Pending Action</div>
            </div>
          </div>

          <div class="stat-card glass-card">
            <div class="icon-box icon-box-sm" style="background: var(--blue-bg); color: var(--blue); border: 1px solid rgba(96,165,250,0.2);">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
            </div>
            <div class="stat-info">
              <div class="stat-num text-blue">{{ stats.contacted }}</div>
              <div class="stat-lbl">Contacted</div>
            </div>
          </div>

          <div class="stat-card glass-card">
            <div class="icon-box icon-box-green icon-box-sm">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="9 11 12 14 22 4"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
            </div>
            <div class="stat-info">
              <div class="stat-num text-success">{{ stats.completed }}</div>
              <div class="stat-lbl">Completed Orders</div>
            </div>
          </div>
        </section>

        <!-- FILTER TABS + SEARCH -->
        <section class="filter-card glass-card">
          <div class="tab-row">
            <button
              *ngFor="let tab of statusTabs"
              class="tab-btn"
              [class.active]="selectedStatus === tab.value"
              (click)="filterByStatus(tab.value)"
            >
              {{ tab.label }}
              <span class="tab-count">{{ getTabCount(tab.value) }}</span>
            </button>
          </div>

          <div class="search-box">
            <span class="search-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </span>
            <input
              type="text"
              class="form-control search-input"
              placeholder="Search name, phone, email…"
              [(ngModel)]="searchQuery"
              (ngModelChange)="onSearchChange()"
            />
          </div>
        </section>

        <!-- TABLE CARD -->
        <section class="glass-card table-card">
          <div *ngIf="loading" class="loading-state">
            <div class="spinner" style="width: 24px; height: 24px; border-width: 3px;"></div>
            <span>Loading enquiries…</span>
          </div>

          <div *ngIf="!loading" class="table-wrapper">
            <table class="custom-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Product & Device</th>
                  <th>City</th>
                  <th>Status</th>
                  <th style="text-align: right;">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let eq of enquiries">
                  <td class="date-cell">
                    <div class="date-main">{{ eq.createdAt | date:'MMM d, y' }}</div>
                    <div class="date-time">{{ eq.createdAt | date:'h:mm a' }}</div>
                  </td>

                  <td>
                    <div class="customer-cell">
                      <div class="customer-avatar">{{ getInitials(eq.name) }}</div>
                      <div class="customer-info">
                        <div class="customer-name">{{ eq.name }}</div>
                        <div class="customer-phone">{{ eq.phone }}</div>
                        <div class="customer-email">{{ eq.email }}</div>
                      </div>
                    </div>
                  </td>

                  <td>
                    <div class="product-info">
                      <div class="product-info-title">{{ eq.productTitle || 'General Customization' }}</div>
                      <span class="badge badge-gold" style="margin-top: 4px; display: inline-flex;">
                        {{ eq.deviceModel || 'iPhone 16 Pro Max' }}
                      </span>
                      <div *ngIf="eq.notes" class="enquiry-notes">"{{ eq.notes }}"</div>
                    </div>
                  </td>

                  <td class="city-cell">
                    <div style="display: flex; align-items: center; gap: 6px; font-size: 13px; color: var(--text-secondary);">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      {{ eq.city || 'N/A' }}
                    </div>
                  </td>

                  <td>
                    <select
                      class="status-select"
                      [value]="eq.status"
                      (change)="onStatusChange(eq, $event)"
                      [ngClass]="getStatusClass(eq.status)"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Contacted">Contacted</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </td>

                  <td style="text-align: right;">
                    <div class="action-group">
                      <a
                        [href]="getWhatsappUrl(eq.phone, eq.name, eq.productTitle)"
                        target="_blank"
                        class="btn-wa"
                        title="WhatsApp customer"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.454 5.709 1.455h.008c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        WhatsApp
                      </a>
                      <button (click)="deleteEnquiry(eq)" class="btn-danger btn-icon" title="Delete enquiry">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                          <path d="M10 11v6M14 11v6"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr *ngIf="enquiries.length === 0">
                  <td colspan="6">
                    <div class="empty-state">
                      <div class="empty-state-icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                      </div>
                      <h4>No enquiries found</h4>
                      <p>No customer enquiries match your current filter</p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

      </main>
    </div>
  `,
  styles: [`
    /* Stats row */
    .stats-row {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 16px;
    }

    @media (max-width: 1100px) {
      .stats-row { grid-template-columns: repeat(2, 1fr); }
    }

    .stat-card {
      padding: 18px 20px;
      display: flex;
      align-items: center;
      gap: 14px;
    }

    .stat-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .stat-num {
      font-size: 26px;
      font-weight: 800;
      letter-spacing: -0.02em;
      line-height: 1;
    }

    .stat-lbl {
      font-size: 11px;
      font-weight: 600;
      color: var(--text-muted);
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    /* Filter card */
    .filter-card {
      padding: 14px 18px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
    }

    .tab-row {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
    }

    .tab-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      border-radius: var(--radius-full);
      font-family: var(--font-sans);
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      border: 1px solid var(--border-color);
      background: transparent;
      color: var(--text-secondary);
      transition: all var(--transition-fast);
    }

    .tab-btn:hover {
      background: rgba(255,255,255,0.05);
      color: var(--text-primary);
    }

    .tab-btn.active {
      background: var(--accent-gold);
      color: #0B0F17;
      border-color: var(--accent-gold);
      font-weight: 700;
    }

    .tab-count {
      font-size: 10px;
      font-weight: 800;
      background: rgba(255,255,255,0.15);
      border-radius: var(--radius-full);
      padding: 1px 6px;
      min-width: 18px;
      text-align: center;
    }

    .tab-btn.active .tab-count {
      background: rgba(0,0,0,0.2);
      color: #0B0F17;
    }

    .search-box {
      position: relative;
      width: 260px;
    }

    .search-icon {
      position: absolute;
      left: 13px;
      top: 50%;
      transform: translateY(-50%);
      color: var(--text-muted);
      display: flex;
      pointer-events: none;
    }

    .search-input {
      padding-left: 38px;
    }

    /* Table card */
    .table-card {
      overflow: hidden;
    }

    .loading-state {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 50px;
      color: var(--text-secondary);
      font-size: 14px;
    }

    /* Customer cell */
    .customer-cell {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .customer-avatar {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      background: linear-gradient(135deg, #C9A96E, #B8924F);
      color: #0B0F17;
      font-size: 12px;
      font-weight: 800;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .customer-info {
      display: flex;
      flex-direction: column;
      gap: 1px;
    }

    .customer-name {
      font-size: 13.5px;
      font-weight: 700;
      color: var(--text-primary);
    }

    .customer-phone {
      font-size: 11.5px;
      color: var(--accent-gold);
      font-weight: 600;
    }

    .customer-email {
      font-size: 11px;
      color: var(--text-muted);
    }

    /* Date cell */
    .date-cell {
      white-space: nowrap;
    }

    .date-main {
      font-size: 12.5px;
      font-weight: 600;
      color: var(--text-secondary);
    }

    .date-time {
      font-size: 11px;
      color: var(--text-muted);
    }

    /* Product info */
    .product-info-title {
      font-size: 13px;
      font-weight: 700;
      color: var(--text-primary);
    }

    .enquiry-notes {
      font-size: 11px;
      color: var(--text-muted);
      font-style: italic;
      margin-top: 4px;
      max-width: 220px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* Action group */
    .action-group {
      display: flex;
      align-items: center;
      gap: 8px;
      justify-content: flex-end;
    }

    /* WhatsApp button */
    .btn-wa {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: #25D366;
      color: #ffffff;
      text-decoration: none;
      font-family: var(--font-sans);
      font-size: 11.5px;
      font-weight: 700;
      padding: 6px 12px;
      border-radius: var(--radius-sm);
      transition: background var(--transition-fast);
    }

    .btn-wa:hover {
      background: #1DA851;
      color: #ffffff;
    }
  `]
})
export class EnquiryListComponent implements OnInit {
  enquiries: EnquiryItem[] = [];
  stats: EnquiryStats = { total: 0, pending: 0, contacted: 0, completed: 0 };
  selectedStatus = 'all';
  searchQuery = '';
  loading = true;

  statusTabs = [
    { label: 'All', value: 'all' },
    { label: 'Pending', value: 'Pending' },
    { label: 'Contacted', value: 'Contacted' },
    { label: 'Completed', value: 'Completed' },
  ];

  constructor(private enquiryService: EnquiryService) {}

  ngOnInit(): void {
    this.loadStats();
    this.loadEnquiries();
  }

  loadStats(): void {
    this.enquiryService.getStats().subscribe({
      next: (res) => (this.stats = res),
      error: (err) => console.error(err)
    });
  }

  loadEnquiries(): void {
    this.loading = true;
    this.enquiryService.getEnquiries(this.selectedStatus, this.searchQuery).subscribe({
      next: (res) => {
        this.enquiries = res.enquiries;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        console.error(err);
      }
    });
  }

  filterByStatus(status: string): void {
    this.selectedStatus = status;
    this.loadEnquiries();
  }

  onSearchChange(): void {
    this.loadEnquiries();
  }

  getTabCount(value: string): number {
    switch (value) {
      case 'all': return this.stats.total;
      case 'Pending': return this.stats.pending;
      case 'Contacted': return this.stats.contacted;
      case 'Completed': return this.stats.completed;
      default: return 0;
    }
  }

  onStatusChange(eq: EnquiryItem, event: Event): void {
    const newStatus = (event.target as HTMLSelectElement).value;
    if (!eq._id) return;
    this.enquiryService.updateStatus(eq._id, newStatus).subscribe({
      next: (updated) => {
        eq.status = updated.status;
        this.loadStats();
      },
      error: (err) => alert('Failed to update status: ' + err.message)
    });
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Pending': return 'status-pending';
      case 'Contacted': return 'status-contacted';
      case 'Completed': return 'status-completed';
      case 'Cancelled': return 'status-cancelled';
      default: return '';
    }
  }

  getInitials(name: string): string {
    return (name || 'U')
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  getWhatsappUrl(phone: string, name: string, productTitle?: string): string {
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    const text = encodeURIComponent(
      `Hello ${name}, thank you for your enquiry on Shell Muze for "${productTitle || 'Custom Phone Case'}". How can we assist with your live 3D proof?`
    );
    return `https://wa.me/${cleanPhone}?text=${text}`;
  }

  deleteEnquiry(eq: EnquiryItem): void {
    if (!eq._id) return;
    if (confirm(`Delete enquiry from "${eq.name}"?`)) {
      this.enquiryService.deleteEnquiry(eq._id).subscribe({
        next: () => {
          this.enquiries = this.enquiries.filter((e) => e._id !== eq._id);
          this.loadStats();
        },
        error: (err) => alert('Failed to delete enquiry: ' + err.message)
      });
    }
  }
}
